import { WSActions, WSAlert, WSList } from "@wingspanhq/fe-component-library";
import React, { useMemo, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { useQueryPayerEngagement } from "../../../../query/payerEngagements/queries/useQueryPayerEngagement";
import { useQueryPayer } from "../../../../query/payers/queries/useQueryPayer";
import { useQueryPayerRows } from "../../../../query/search/payer/queries/useQueryPayerRows";
import { useQueryEngagementsWorkDefinitions } from "../../../../query/workDefinition/queries/useQueryEngagementWorkDefinitions";
import { useQueryWorkItems } from "../../../../query/workItem/queries/useQueryWorkItems";
import { useQueryWorkLog } from "../../../../query/workLog/queries/useQueryWorkLog";
import { Layout } from "../../../../shared/components/Layout";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { getPayerName } from "../../../Payers/utils/getPayerNames";
import { CardWorkItems } from "../../components/CardWorkItems";
import { filterActiveWorkItems } from "../../utils/filterActiveWorkItems";
import { WorkLogEngagementDetails } from "../../components/WorkLogEngagementDetails";
import { useModalConvert } from "../../modals/ModalConvert";
import { useModalExit } from "./ModalExit";
import { useQueryInvoicingConfigForPayee } from "../../../../query/invoicingConfig/queries/useQueryInvoicingConfigForPayee";
import { selectorIsPayeeAllowedToInitiateInvoice } from "../../../../Invoices/selectors/selectorIsPayeeAllowedToInitiateInvoice";
import { AlertClientNotAllowedSubmission } from "../../../../Invoices/components/AlertClientNotAllowedSubmission";
import { useUserId } from "../../../../query/hooks/helpers";

export const RouteEdit: React.FC<
  RouteComponentProps<{ workLogId: string }>
> = props => {
  const userId = useUserId();
  const history = useHistory();
  const workLogId = props.match.params.workLogId;
  const queryWorkLog = useQueryWorkLog(workLogId);
  const workLog = queryWorkLog.data;

  const queryPayerRow = useQueryPayerRows(
    {
      filter: {
        "engagements.payerPayeeEngagementId": workLog?.payerPayeeEngagementId
      }
    },
    {
      enabled: !!workLog?.payerPayeeEngagementId
    }
  );

  const payerId = queryPayerRow.data?.[0]?.payerId;
  const payerPayeeEngagementId = workLog?.payerPayeeEngagementId;

  const queryPayer = useQueryPayer(payerId!, {
    enabled: !!payerId
  });
  const queryPayerPayeeEngagement = useQueryPayerEngagement(
    payerId!,
    payerPayeeEngagementId!,
    {
      enabled: !!payerId && !!payerPayeeEngagementId
    }
  );
  const payer = queryPayer.data;
  const payerPayeeEngagement = queryPayerPayeeEngagement.data;

  const queryWorkDefinitions = useQueryEngagementsWorkDefinitions(
    {
      payerId: payerId!,
      engagementId: payerPayeeEngagementId!,
      sort: { name: "asc" }
    },
    {
      enabled: !!(payerId && payerPayeeEngagementId)
    }
  );

  const queryInvoicingConfigForPayee = useQueryInvoicingConfigForPayee(
    payerId as string,
    {
      enabled: !!payerId
    }
  );

  const isPayeeNotAllowedToInitiateInvoice =
    !selectorIsPayeeAllowedToInitiateInvoice(
      queryInvoicingConfigForPayee.data,
      userId
    );

  const workDefinitions = queryWorkDefinitions.data || [];

  const browserTitle = useMemo(() => {
    if (!payerPayeeEngagementId) {
      return "Log Work";
    }

    if (!payer) {
      return "Log Work";
    }

    const payerName = getPayerName(payer);

    if (payerPayeeEngagement) {
      return `Log Work – ${payerName} • ${payerPayeeEngagement.engagementName}`;
    }

    return `Log Work – ${payerName}`;
  }, [payer, payerPayeeEngagement, payerPayeeEngagementId]);

  useBrowserPageTitle(browserTitle);

  const queryWorkItems = useQueryWorkItems({
    "filter[workLogId][=]": workLogId
  });

  const modalExit = useModalExit();
  const modalConvert = useModalConvert();

  const [createError, setCreateError] = useState(false);

  const isInvoiceCreated = !!workLog?.invoiceId;

  return (
    <Layout
      title="Work log"
      onClose={() => {
        modalExit.open();
      }}
    >
      <WSList gap="2XL">
        <WorkLogEngagementDetails
          shimmer={queryPayerPayeeEngagement.isInitialLoading}
          payer={payer}
          payerPayeeEngagement={payerPayeeEngagement}
        />

        {isPayeeNotAllowedToInitiateInvoice && (
          <AlertClientNotAllowedSubmission />
        )}

        <CardWorkItems
          shimmer={
            queryWorkItems.isInitialLoading ||
            queryWorkDefinitions.isInitialLoading
          }
          noEdit={isInvoiceCreated}
          workLogId={workLogId}
          payerId={payerId!}
          payerPayeeEngagementId={payerPayeeEngagementId!}
          workItems={queryWorkItems.data || []}
          workDefinitions={workDefinitions}
          resources={{
            payerPayeeEngagement
          }}
        />

        {createError && (
          <WSAlert
            title="Invoice creation error"
            theme="warning"
            icon="alert"
            onDismiss={() => {
              setCreateError(false);
            }}
          >
            There was an error creating the invoice. Please try again. If the
            issue persists, please contact support.
          </WSAlert>
        )}

        <WSActions
          alignment="fill"
          disabled={isPayeeNotAllowedToInitiateInvoice}
          buttons={[
            {
              label: isInvoiceCreated ? "View invoice" : "Create invoice",
              disabled:
                filterActiveWorkItems(queryWorkItems.data || []).length === 0,
              onClick: () => {
                if (isInvoiceCreated) {
                  history.push(`/member/invoices/${workLog.invoiceId}`);
                } else {
                  modalConvert.open({
                    workLogId,
                    onClose: result => {
                      setCreateError(result?.createError || false);
                    }
                  });
                }
              }
            }
          ]}
        />
      </WSList>
    </Layout>
  );
};
